import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useAuth } from '../../lib/auth-context.js';
import { useScenarioDetail } from './hooks.js';

/**
 * Scenario-notes wiring. The engine returns notes embedded in the
 * scenario detail response (`GET /v1/scenarios/{fusionId}.notes[]`)
 * — there is NO separate list endpoint, so the SPA derives the
 * collection from `useScenarioDetail` instead of firing a second
 * read. POST / PUT / DELETE go through the engine's per-note
 * routes (which DO exist), and `onSuccess` invalidates the detail
 * query so the next render picks up the mutated set.
 *
 * Engine row shape:
 *   { id, noteType, noteText, createdBy, createdAt, modifiedBy?, modifiedAt? }
 */
export interface ScenarioNote {
  id: number;
  noteType: string;
  noteText: string;
  createdBy: string;
  createdAt: string;
  modifiedBy?: string;
  modifiedAt?: string;
}

function asScenarioNote(raw: unknown): ScenarioNote | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'number') return null;
  return {
    id: r.id,
    noteType: typeof r.noteType === 'string' ? r.noteType : 'general',
    noteText: typeof r.noteText === 'string' ? r.noteText : '',
    createdBy: typeof r.createdBy === 'string' ? r.createdBy : '',
    createdAt: typeof r.createdAt === 'string' ? r.createdAt : '',
    modifiedBy: typeof r.modifiedBy === 'string' ? r.modifiedBy : undefined,
    modifiedAt: typeof r.modifiedAt === 'string' ? r.modifiedAt : undefined,
  };
}

export function useScenarioNotes(scenarioId: string | undefined): {
  data?: ReadonlyArray<ScenarioNote>;
  isLoading: boolean;
  error: Error | null;
} {
  const { data: detail, isLoading, error } = useScenarioDetail(scenarioId);
  const rawNotes = (detail?.notes ?? []) as ReadonlyArray<unknown>;
  const notes = rawNotes.map(asScenarioNote).filter((n): n is ScenarioNote => n !== null);
  return { data: notes, isLoading, error };
}

function useDetailInvalidator(scenarioId: string | undefined): () => void {
  const { session } = useAuth();
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({
      queryKey: ['scenarios', 'detail', session?.licenseId ?? 'anon', scenarioId ?? ''],
    });
  };
}

export function useCreateScenarioNote(scenarioId: string | undefined): {
  create: (text: string, noteType?: string) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
} {
  const { api } = useAuth();
  const invalidateDetail = useDetailInvalidator(scenarioId);
  const m = useMutation({
    mutationFn: (args: { text: string; noteType: string }) =>
      api.post<unknown>('/v1/scenario-notes', {
        fusionScenarioId: scenarioId,
        noteType: args.noteType,
        noteText: args.text,
      }),
    onSuccess: () => {
      invalidateDetail();
      toast.success('Note added');
    },
    onError: (err: Error) => toast.error(`Could not add note: ${err.message}`),
  });
  return {
    create: (text, noteType = 'general') => m.mutateAsync({ text, noteType }),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

export function useUpdateScenarioNote(scenarioId: string | undefined): {
  save: (noteId: number, text: string) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
} {
  const { api } = useAuth();
  const invalidateDetail = useDetailInvalidator(scenarioId);
  const m = useMutation({
    mutationFn: (args: { noteId: number; text: string }) =>
      api.put<unknown>(`/v1/scenario-notes/${args.noteId}`, {
        noteText: args.text,
      }),
    onSuccess: () => {
      invalidateDetail();
      toast.success('Note updated');
    },
    onError: (err: Error) => toast.error(`Could not update note: ${err.message}`),
  });
  return {
    save: (noteId, text) => m.mutateAsync({ noteId, text }),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}

export function useDeleteScenarioNote(scenarioId: string | undefined): {
  remove: (noteId: number) => Promise<unknown>;
  isPending: boolean;
  error: Error | null;
} {
  const { api } = useAuth();
  const invalidateDetail = useDetailInvalidator(scenarioId);
  const m = useMutation({
    mutationFn: (noteId: number) => api.delete<unknown>(`/v1/scenario-notes/${noteId}`),
    onSuccess: () => {
      invalidateDetail();
      toast.success('Note deleted');
    },
    onError: (err: Error) => toast.error(`Could not delete note: ${err.message}`),
  });
  return {
    remove: (noteId) => m.mutateAsync(noteId),
    isPending: m.isPending,
    error: (m.error as Error) ?? null,
  };
}
